import { useState, useCallback } from 'react';
import { GridCellParams } from '@mui/x-data-grid-premium';
import Actions from '../../../store/Actions';
import { getRowId } from '../utils';
import {
  OTCRow,
  UseOTCIllioProps,
  UseOTCIllioReturn,
  UpdatedCellsMap,
  CellStatusMap,
  IllioValuesMap,
  ComparisonDiff,
  DAYS_SINCE_ILLIO_UPDATE,
} from '../types';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';
import { COLUMN_FIELDS, CSS_CLASSES, DATE_FORMATS } from '../OTCConstants';
import { parseNumberWithCommas } from '../../../utils/numberFormatter';
import { OTCComparisonResponse } from '../../../types/generatedTypes';
import {
  useOTCSyncProgress,
  useOTCCheckDifferencesProgress,
} from '../../../store/otcProgressStore';
import { useOTCSyncSSE } from '../components/OTCSyncProgress/useOTCSyncSSE';
import { useOTCCheckDifferencesSSE } from '../components/OTCCheckProgress/useOTCCheckDifferencesSSE';

/**
 * Extracts differences from an OTC comparison response.
 * Returns an array of field differences with their status and Illio values.
 */
const extractDifferencesFromComparison = (
  comparisonData: OTCComparisonResponse
): ComparisonDiff[] => {
  const diffs: ComparisonDiff[] = [];

  if (comparisonData.prices) {
    Object.entries(comparisonData.prices).forEach(([dateKey, data]) => {
      if (data.status !== "identical") {
        diffs.push({
          field: dateKey,
          status: data.status,
          illioValue: data.illio_value ?? null,
        });
      }
    });
  }

  if (comparisonData.currency && comparisonData.currency.status !== "identical") {
    diffs.push({
      field: COLUMN_FIELDS.CURRENCY,
      status: comparisonData.currency.status,
      illioValue: comparisonData.currency.illio_value ?? null,
    });
  }

  if (comparisonData.illio_name && comparisonData.illio_name.status !== "identical") {
    diffs.push({
      field: COLUMN_FIELDS.ILLIO_NAME,
      status: comparisonData.illio_name.status,
      illioValue: comparisonData.illio_name.illio_value ?? null,
    });
  }

  if ((comparisonData as any).instrument_status && (comparisonData as any).instrument_status.status !== "identical") {
    diffs.push({
      field: COLUMN_FIELDS.INSTRUMENT_STATUS,
      status: (comparisonData as any).instrument_status.status,
      illioValue: (comparisonData as any).instrument_status.illio_value ?? null,
    });
  }

  return diffs;
};

export const useOTCIllio = ({
  otcData,
  originalData,
  selectedRows,
  setOTCData,
  setOriginalData,
  setSelectedRows,
  fetchOTCData,
  calculateDaysSinceIllioUpdate,
  showInactiveInstruments,
}: UseOTCIllioProps): UseOTCIllioReturn => {
  // Illio integration state
  const [updatedCells, setUpdatedCells] = useState<UpdatedCellsMap>({});
  const [cellStatusMap, setCellStatusMap] = useState<CellStatusMap>({});
  const [illioValuesMap, setIllioValuesMap] = useState<IllioValuesMap>({});
  const [illioCheckLoading, setIllioCheckLoading] = useState(false);
  const [illioUploadLoading, setIllioUploadLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [showOnlyIllioDifferences, setShowOnlyIllioDifferences] =
    useState<boolean>(false);
  const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
  const [failedOtcIds, setFailedOtcIds] = useState<string[]>([]);

  // SSE progress tracking for sync
  const { start: startSync, reset: resetProgress } = useOTCSyncProgress();
  const { connect: connectSSE, disconnect: disconnectSSE } = useOTCSyncSSE();

  // SSE progress tracking for check differences
  const {
    start: startCheck,
    reset: resetCheckProgress,
    complete: completeCheck,
  } = useOTCCheckDifferencesProgress();
  const { connect: connectCheckSSE, disconnect: disconnectCheckSSE } =
    useOTCCheckDifferencesSSE();

  // Check all rows for Illio differences using batch endpoint with SSE progress
  const checkAllIllioDifferences = useCallback(async () => {
    try {
      setIllioCheckLoading(true);
      setFailedOtcIds([]);

      // Clear existing indicators
      setUpdatedCells({});
      setCellStatusMap({});
      setIllioValuesMap({});

      resetCheckProgress(); // Clear any stale progress data from previous runs
      startCheck(); // Start progress tracking
      connectCheckSSE(); // Connect to SSE stream

      // Call the batch endpoint
      const response = await Actions.checkAllOTCDifferences();

      // Process the comparison results regardless of success flag
      // This allows showing partial results even if some checks failed
      const newUpdatedCells: UpdatedCellsMap = {};
      const newCellStatusMap: CellStatusMap = {};
      const newIllioValuesMap: IllioValuesMap = {};

      if (response.comparison_results) {
        // Find corresponding row for each otc_id in results
        Object.entries(response.comparison_results).forEach(
          ([otcId, comparisonData]) => {
            const row = otcData.find(r => r.otc_id === otcId);
            if (!row) return;

            const rowId = getRowId(row);
            const typedData = comparisonData as OTCComparisonResponse;

            // Extract differences using helper
            const diffs = extractDifferencesFromComparison(typedData);

            if (diffs.length > 0) {
              newUpdatedCells[rowId] = new Set<string>();
              newCellStatusMap[rowId] = {};
              newIllioValuesMap[rowId] = {};

              diffs.forEach(({ field, status, illioValue }) => {
                newUpdatedCells[rowId].add(field);
                newCellStatusMap[rowId][field] = status;
                newIllioValuesMap[rowId][field] = illioValue;
              });
            }
          }
        );
      }

      setFailedOtcIds((response as any).failed_otc_ids ?? []);
      setUpdatedCells(newUpdatedCells);
      setCellStatusMap(newCellStatusMap);
      setIllioValuesMap(newIllioValuesMap);

      // Update days since update after setting new updated cells
      setOTCData(prevData =>
        prevData.map(row => ({
          ...row,
          [DAYS_SINCE_ILLIO_UPDATE]: calculateDaysSinceIllioUpdate(
            row,
            getRowId(row),
            newUpdatedCells
          ),
        }))
      );

      // Complete the progress tracking
      completeCheck();

      // Show appropriate snackbar based on operation status
      if (response.status === 'partial') {
        showSnackbar({
          title: 'Illio Check Complete (Partial)',
          type: SnackbarType.warning,
          message: `Checked ${response.total_checked} instruments, ${response.errors_count} had errors`,
        });
      } else if (response.status === 'success') {
        showSnackbar({
          title: 'Illio Check Complete',
          type: SnackbarType.success,
          message: 'Differences with Illio have been identified',
        });
      } else {
        // status === "failed" - no results at all
        showSnackbar({
          title: 'Illio Check Failed',
          type: SnackbarType.error,
          message: response.message || 'Failed to check differences with Illio',
        });
      }
    } catch (error) {
      console.error('Error checking Illio differences:', error);
      showSnackbar({
        title: 'Error',
        type: SnackbarType.error,
        message: 'Failed to check differences with Illio',
      });
      resetCheckProgress(); // Reset on error
    } finally {
      setIllioCheckLoading(false);
      disconnectCheckSSE(); // Cleanup SSE connection
    }
  }, [
    otcData,
    calculateDaysSinceIllioUpdate,
    setOTCData,
    startCheck,
    connectCheckSSE,
    disconnectCheckSSE,
    resetCheckProgress,
    completeCheck,
  ]);

  const retryFailedOtcCheck = useCallback(async () => {
    if (failedOtcIds.length === 0) return;
    try {
      setIllioCheckLoading(true);
      connectCheckSSE();
      const retryResponse = await Actions.checkOTCDifferencesRetry(failedOtcIds);
      setFailedOtcIds((retryResponse as any).failed_otc_ids ?? []);

      if (retryResponse.comparison_results) {
        setUpdatedCells(prev => {
          const merged = { ...prev };
          Object.entries(retryResponse.comparison_results!).forEach(([otcId, comparisonData]) => {
            const row = otcData.find(r => r.otc_id === otcId);
            if (!row) return;
            const rowId = getRowId(row);
            const diffs = extractDifferencesFromComparison(comparisonData as OTCComparisonResponse);
            if (diffs.length > 0) {
              merged[rowId] = new Set<string>(diffs.map(d => d.field));
            } else {
              delete merged[rowId];
            }
          });
          return merged;
        });

        setCellStatusMap(prev => {
          const merged = { ...prev };
          Object.entries(retryResponse.comparison_results!).forEach(([otcId, comparisonData]) => {
            const row = otcData.find(r => r.otc_id === otcId);
            if (!row) return;
            const rowId = getRowId(row);
            const diffs = extractDifferencesFromComparison(comparisonData as OTCComparisonResponse);
            if (diffs.length > 0) {
              merged[rowId] = Object.fromEntries(diffs.map(d => [d.field, d.status]));
            } else {
              delete merged[rowId];
            }
          });
          return merged;
        });

        setIllioValuesMap(prev => {
          const merged = { ...prev };
          Object.entries(retryResponse.comparison_results!).forEach(([otcId, comparisonData]) => {
            const row = otcData.find(r => r.otc_id === otcId);
            if (!row) return;
            const rowId = getRowId(row);
            const diffs = extractDifferencesFromComparison(comparisonData as OTCComparisonResponse);
            if (diffs.length > 0) {
              merged[rowId] = Object.fromEntries(diffs.map(d => [d.field, d.illioValue]));
            } else {
              delete merged[rowId];
            }
          });
          return merged;
        });
      }

      if (retryResponse.status === 'success') {
        showSnackbar({ title: 'Retry Complete', type: SnackbarType.success, message: 'All failed OTCs checked successfully' });
      } else if (retryResponse.status === 'partial') {
        showSnackbar({ title: 'Retry Partial', type: SnackbarType.warning, message: `${retryResponse.errors_count} OTCs still failed` });
      } else {
        showSnackbar({ title: 'Retry Failed', type: SnackbarType.error, message: retryResponse.message || 'Retry check failed' });
      }
    } catch (error) {
      console.error('Error retrying OTC check:', error);
      showSnackbar({ title: 'Error', type: SnackbarType.error, message: 'Failed to retry OTC check' });
    } finally {
      setIllioCheckLoading(false);
      disconnectCheckSSE();
    }
  }, [failedOtcIds, otcData, connectCheckSSE, disconnectCheckSSE]);

  // Check single row for Illio differences
  const checkDifferenceWithIllioForRow = useCallback(
    async (row: OTCRow, updatedFields?: Set<string>) => {
      try {
        // Skip rows without otc_id
        if (!row.otc_id) return;

        // Get comparison data from server
        const comparisonData = await Actions.getUpdatedOTCComparedToIllio(
          row.otc_id
        );

        if (comparisonData && comparisonData.prices) {
          const rowId = getRowId(row);
          const diffs = extractDifferencesFromComparison(comparisonData);

          // Clear updated fields and apply new differences to updatedCells
          setUpdatedCells((prev) => {
            const newCells = { ...prev };

            // Initialize row if not exists
            if (!newCells[rowId]) {
              newCells[rowId] = new Set<string>();
            } else {
              newCells[rowId] = new Set(newCells[rowId]);
            }

            // Clear fields that were updated
            if (updatedFields) {
              updatedFields.forEach((field) => newCells[rowId].delete(field));
            } else {
              newCells[rowId].clear();
            }

            // Add fields with differences
            diffs.forEach(({ field }) => newCells[rowId].add(field));

            // Remove row if empty
            if (newCells[rowId].size === 0) {
              delete newCells[rowId];
            }

            return newCells;
          });

          // Clear updated fields and apply new differences to cellStatusMap
          setCellStatusMap((prev) => {
            const newStatusMap = { ...prev };

            // Initialize row if not exists
            if (!newStatusMap[rowId]) {
              newStatusMap[rowId] = {};
            } else {
              newStatusMap[rowId] = { ...newStatusMap[rowId] };
            }

            // Clear fields that were updated
            if (updatedFields) {
              updatedFields.forEach((field) => delete newStatusMap[rowId][field]);
            } else {
              newStatusMap[rowId] = {};
            }

            // Add status for fields with differences
            diffs.forEach(({ field, status }) => {
              newStatusMap[rowId][field] = status;
            });

            // Remove row if empty
            if (Object.keys(newStatusMap[rowId]).length === 0) {
              delete newStatusMap[rowId];
            }

            return newStatusMap;
          });

          // Clear updated fields and apply new differences to illioValuesMap
          setIllioValuesMap((prev) => {
            const newValuesMap = { ...prev };

            // Initialize row if not exists
            if (!newValuesMap[rowId]) {
              newValuesMap[rowId] = {};
            } else {
              newValuesMap[rowId] = { ...newValuesMap[rowId] };
            }

            // Clear fields that were updated
            if (updatedFields) {
              updatedFields.forEach((field) => delete newValuesMap[rowId][field]);
            } else {
              newValuesMap[rowId] = {};
            }

            // Add Illio values for fields with differences
            diffs.forEach(({ field, illioValue }) => {
              newValuesMap[rowId][field] = illioValue;
            });

            // Remove row if empty
            if (Object.keys(newValuesMap[rowId]).length === 0) {
              delete newValuesMap[rowId];
            }

            return newValuesMap;
          });
        }
      } catch (error) {
        console.error(
          `Failed to get comparison data for row ${row.id}:`,
          error
        );
      }
    },
    []
  );

  // Handle Illio upload
  const handleIllioUpload = useCallback(async () => {
    try {
      setIllioUploadLoading(true);

      // If rows are selected, upload only selected rows
      // Otherwise, fall back to the current behavior with changed rows
      const rowsToUpload =
        selectedRows.length > 0 ? selectedRows : Object.keys(updatedCells);

      // Find the corresponding row data
      const rowsToSend = otcData.filter(row =>
        rowsToUpload.includes(getRowId(row))
      );

      if (rowsToSend.length > 0) {
        // Format each row the same way as updateServerWithRow
        const rowsForServer = rowsToSend.map(row => {
          // Exclude PNL and days since update from server data
          const {
            id, // eslint-disable-line @typescript-eslint/no-unused-vars
            [COLUMN_FIELDS.PNL]: pnl, // eslint-disable-line @typescript-eslint/no-unused-vars
            [DAYS_SINCE_ILLIO_UPDATE]: daysSince, // eslint-disable-line @typescript-eslint/no-unused-vars
            ...rowData
          } = row;

          // Extract date fields and create prices object
          const prices: Record<string, number> = {};
          const nonDateFields: Record<string, unknown> = {};

          Object.entries(rowData).forEach(([key, value]) => {
            if (DATE_FORMATS.YYYY_MM_DD_REGEX.test(key)) {
              // This is a date field
              if (
                value !== null &&
                value !== '' &&
                !isNaN(parseNumberWithCommas(String(value)))
              ) {
                // Only add to prices if value is not null, empty, or NaN
                prices[key] = parseNumberWithCommas(String(value));
              }
              // Skip adding null/empty date fields to prices object completely
            } else {
              // This is not a date field, add to nonDateFields regardless of value
              nonDateFields[key] = value;
            }
          });

          // Return row in the correct format for server
          return {
            ...nonDateFields,
            nav_prices: prices,
          };
        });

        // Send all selected rows to server
        await Actions.uploadOTCToIllio(rowsForServer);

        // Show success message
        showSnackbar({
          title: `Illio has been updated`,
          type: SnackbarType.success,
          message: `Successfully uploaded ${rowsToSend.length} rows to Illio`,
        });

        // Reset highlights for these rows
        const newUpdatedCells = { ...updatedCells };
        rowsToUpload.forEach(id => {
          delete newUpdatedCells[id];
        });
        setUpdatedCells(newUpdatedCells);

        // Reset original data to current data for these rows
        const newOriginalValues = { ...originalData };
        rowsToSend.forEach(row => {
          newOriginalValues[getRowId(row)] = { ...row };
        });
        setOriginalData(newOriginalValues);

        // Clear selection after upload
        setSelectedRows([]);
      }
    } catch (error) {
      console.error('Error uploading to Illio:', error);
      showSnackbar({
        title: 'Upload Failed',
        type: SnackbarType.error,
        message: 'Failed to upload OTC data to Illio. Please try again.',
      });
    } finally {
      setIllioUploadLoading(false);
    }
  }, [
    selectedRows,
    updatedCells,
    otcData,
    originalData,
    setOriginalData,
    setSelectedRows,
  ]);

  // Open sync confirmation modal
  const handleSyncClick = useCallback(() => {
    setShowSyncConfirmation(true);
  }, []);

  // Handle sync from Illio (after confirmation)
  const confirmSyncFromIllio = useCallback(async () => {
    setShowSyncConfirmation(false);
    try {
      setSyncLoading(true);
      resetProgress(); // Clear any stale progress data from previous runs
      startSync(); // Start progress tracking
      connectSSE(); // Connect to SSE stream

      // First, download JSON backup before syncing
      try {
        await Actions.downloadOTCBackup();
      } catch (backupError) {
        console.warn('Failed to download backup before sync:', backupError);
      }

      // Then proceed with sync (SSE will update progress)
      const result = await Actions.syncOTCFromIllio();

      // Clear all Illio comparison indicators since data is now synced
      clearAllIllioComparisons();

      // Refresh OTC data (includes updated last_sync)
      await fetchOTCData(showInactiveInstruments);
    } catch (error) {
      console.error('Error syncing OTC from Illio:', error);
      showSnackbar({
        title: 'Sync Failed',
        type: SnackbarType.error,
        message: 'Failed to sync OTC from Illio',
      });
      resetProgress(); // Reset on error
    } finally {
      setSyncLoading(false);
      disconnectSSE(); // Cleanup SSE connection
    }
  }, [
    fetchOTCData,
    startSync,
    connectSSE,
    disconnectSSE,
    resetProgress,
    showInactiveInstruments,
  ]);

  // Get cell styling based on Illio status
  const getCellClassName = useCallback(
    (params: GridCellParams) => {
      const rowId = params.id.toString();
      const field = params.field;

      // Never highlight the PNL column
      if (field === COLUMN_FIELDS.PNL) {
        return '';
      }

      // Check if this cell has difference info
      if (updatedCells[rowId]?.has(field)) {
        // Get the status from cellStatusMap
        const status = cellStatusMap[rowId]?.[field];

        if (status) {
          // Return appropriate class based on status
          switch (status) {
            case 'added':
              return CSS_CLASSES.HIGHLIGHTED_CELL_ADDED;
            case 'removed':
              return CSS_CLASSES.HIGHLIGHTED_CELL_REMOVED;
            case 'changed':
              return CSS_CLASSES.HIGHLIGHTED_CELL_CHANGED;
          }
        }

        // Fallback to generic highlighting if status not found
      }

      // Return empty string if no differences
      return '';
    },
    [updatedCells, cellStatusMap]
  );

  // Toggle Illio differences filter
  const toggleIllioDifferencesFilter = useCallback(() => {
    setShowOnlyIllioDifferences(prev => !prev);
  }, []);

  // Clear updated cells for specific rows - replacement for exposed setters
  const clearUpdatedCellsForRows = useCallback((rowIds: string[]) => {
    setUpdatedCells(prev => {
      const newUpdatedCells = { ...prev };
      rowIds.forEach(id => {
        delete newUpdatedCells[id];
      });
      return newUpdatedCells;
    });
    setCellStatusMap(prev => {
      const newStatusMap = { ...prev };
      rowIds.forEach(id => {
        delete newStatusMap[id];
      });
      return newStatusMap;
    });
    setIllioValuesMap(prev => {
      const newValuesMap = { ...prev };
      rowIds.forEach(id => {
        delete newValuesMap[id];
      });
      return newValuesMap;
    });
  }, []);

  // Clear all Illio comparison state
  const clearAllIllioComparisons = useCallback(() => {
    setUpdatedCells({});
    setCellStatusMap({});
    setIllioValuesMap({});
  }, []);

  // Clear indicator for a specific cell
  const clearIndicatorForCell = useCallback((rowId: string, field: string) => {
    setUpdatedCells(prev => {
      const newCells = { ...prev };
      if (newCells[rowId]) {
        newCells[rowId] = new Set(newCells[rowId]);
        newCells[rowId].delete(field);
        if (newCells[rowId].size === 0) {
          delete newCells[rowId];
        }
      }
      return newCells;
    });
    setCellStatusMap(prev => {
      const newMap = { ...prev };
      if (newMap[rowId]) {
        newMap[rowId] = { ...newMap[rowId] };
        delete newMap[rowId][field];
        if (Object.keys(newMap[rowId]).length === 0) {
          delete newMap[rowId];
        }
      }
      return newMap;
    });
    setIllioValuesMap(prev => {
      const newMap = { ...prev };
      if (newMap[rowId]) {
        newMap[rowId] = { ...newMap[rowId] };
        delete newMap[rowId][field];
        if (Object.keys(newMap[rowId]).length === 0) {
          delete newMap[rowId];
        }
      }
      return newMap;
    });
  }, []);

  return {
    updatedCells,
    cellStatusMap,
    illioValuesMap,
    illioCheckLoading,
    illioUploadLoading,
    syncLoading,
    showOnlyIllioDifferences,
    setShowOnlyIllioDifferences,
    showSyncConfirmation,
    setShowSyncConfirmation,
    checkAllIllioDifferences,
    failedOtcIds,
    retryFailedOtcCheck,
    checkDifferenceWithIllioForRow,
    handleIllioUpload,
    handleSyncClick,
    confirmSyncFromIllio,
    getCellClassName,
    toggleIllioDifferencesFilter,
    clearUpdatedCellsForRows,
    clearAllIllioComparisons,
    clearIndicatorForCell,
  };
};
